import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

import eq from 'webapp/helpers/eq';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import FormInputComponent from 'webapp/components/form/input';
import SubmitButtonComponent from 'webapp/components/submit-button';

interface ReviewProvider {
	name: string;
	url: string;
	logo: string;
	logoWidth: number;
}

type Args = Record<string, never>;

export default class NpsFormComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('request') declare requestService: Service.Request;

	@tracked step = 1;
	@tracked selectedScore: number | null = null;
	@tracked selectedChoices: string[] = [];
	@tracked feedback = '';
	@tracked selectedReviewProvider: ReviewProvider | null = null;
	@tracked isFeedbackInputShown = false;
	@tracked submitInProgress = false;

	wasFeedbackSuccessfullySubmitted = false;

	// Generating score numbers
	scores = [...Array(11).keys()];

	choices = [
		{
			name: 'more_metrics',
			value: 'more metrics',
		},
		{
			name: 'more_features',
			value: 'more features',
		},
		{
			name: 'more_integrationse',
			value: 'more integrations',
		},
		{
			name: 'faster_loading_times',
			value: 'faster loading times',
		},
		{
			name: 'more_value_for_money',
			value: 'more value for money',
		},
		{
			name: 'more_frequent_sync_intervals',
			value: 'more frequent sync intervals',
		},
		{
			name: 'other',
			value: 'other',
		},
	];

	get choicesWithSelection() {
		return this.choices.map((choice) => ({
			...choice,
			selected: this.selectedChoices.includes(choice.value),
		}));
	}

	reviewProviders = [
		{
			name: 'Capterra',
			url: 'https://www.capterra.com/p/154024/Databox/reviews',
			logo: 'capterra.svg',
			logoWidth: 100,
		},
		{
			name: 'G2',
			url: 'https://www.g2.com/products/databox-inc-databox/reviews',
			logo: 'g2.svg',
			logoWidth: 35,
		},
	];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// Show 3rd option only if any of HubSpot sources is connected
		// Hubspot, HubspotCrm, HubspotServices
		const hubspotSourceTypesIds = this.mmSourceTypesService.sourceTypeModels
			.filter((sourceTypeModel) =>
				['Hubspot', 'HubspotCrm', 'HubspotServices'].includes(sourceTypeModel.key),
			)
			.map((item) => item.id);
		if (
			this.mmSourcesService.connectedSourceTypeIdsOrKeys.some((alreadyConnectedType) =>
				hubspotSourceTypesIds.includes(alreadyConnectedType as number),
			)
		) {
			this.reviewProviders.push({
				name: 'HubSpot',
				url: 'https://ecosystem.hubspot.com/marketplace/apps/marketing/analytics-data/databox',
				logo: 'hubspot.svg',
				logoWidth: 100,
			});
		}

		const selectedReviewProviderId =
			(this.currentUserService.npsFeedback?.maxScoreCount || 0) % this.reviewProviders.length;
		this.selectedReviewProvider = this.reviewProviders[selectedReviewProviderId] || null;

		this.analyticsService.event(TrackingEvent.NPS_SHOWN);
	}

	get isStep1(): boolean {
		return this.step === 1;
	}

	get isScoreSelected(): boolean {
		return this.selectedScore !== null;
	}

	get isScoreSixOrLess(): boolean {
		return this.isScoreSelected && this.selectedScore! <= 6;
	}

	get isScoreInBetween(): boolean {
		const score = this.selectedScore!;

		return this.isScoreSelected && score > 6 && score < 9;
	}

	get isScoreNineOrMore(): boolean {
		return this.isScoreSelected && this.selectedScore! >= 9;
	}

	get title(): string {
		return this.isStep1
			? 'How likely are you to recommend Databox to a friend or colleague?'
			: "Sounds like you've had a great experience with Databox!";
	}

	get canRequestSetupHelp(): boolean {
		return !this.currentUserService.isClientUserInPaidClientSpace;
	}

	markDone(): void {
		this.currentUserService.isEligibleForNpsFormToBeShown = false;
	}

	@action
	selectScore(value: number): void {
		if (this.isScoreSelected) {
			return;
		}

		this.selectedScore = value;

		if (!this.isScoreSixOrLess) {
			this.submitFeedback();
		}
	}

	@action
	selectChoice(value: string): void {
		if (this.selectedChoices.includes(value)) {
			this.selectedChoices = this.selectedChoices.filter((choice) => choice !== value);
		} else {
			this.selectedChoices = [...this.selectedChoices, value];
		}

		this.isFeedbackInputShown = this.selectedChoices.includes('other');
	}

	@action
	openRequestSetupHelp(): void {
		this.analyticsService.event(TrackingEvent.NPS_BOOK_A_CALL);
		this.accountService.requestSetupHelp();
		this.markDone();
	}

	@action
	openReviewProviderUrl(): void {
		if (!this.selectedReviewProvider) {
			return;
		}

		window.open(this.selectedReviewProvider.url, '_blank');
		this.analyticsService.event(TrackingEvent.NPS_FEEDBACK_CTA_CLICKED, {
			marketplace: this.selectedReviewProvider.name,
		});

		this.markDone();
	}

	@action
	submitFeedback(isSkipped = false): void {
		this.submitInProgress = true;

		// cleanup fields if filled up before but then higher score than 6 is selected (currently switching between scores is disabled ...)
		if (!this.isScoreSixOrLess) {
			this.selectedChoices = [];
			this.feedback = '';
		}

		if (!this.isFeedbackInputShown) {
			this.feedback = '';
		}

		let data;
		if (isSkipped) {
			this.analyticsService.event(TrackingEvent.NPS_SKIPPED);
			data = { isSkipped: true };
		} else {
			this.analyticsService.event(TrackingEvent.NPS_SCORE_COMPLETED, {
				value: this.selectedScore,
			});
			data = {
				score: this.selectedScore,
				choices: this.selectedChoices,
				feedback: this.feedback,
			};
		}

		this.requestService
			.api('POST', '/tracking/feedback', data)
			.then(() => {
				if (!isSkipped) {
					if (this.selectedChoices.length || this.feedback.length) {
						this.analyticsService.event(TrackingEvent.NPS_FEEDBACK_COMPLETED, {
							text: this.feedback,
							choices: this.selectedChoices,
						});
					}

					this.step = 2;
				}

				this.wasFeedbackSuccessfullySubmitted = true;
			})
			.finally(() => {
				this.submitInProgress = false;
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	close(): void {
		if (!this.wasFeedbackSuccessfullySubmitted && !this.isScoreSelected) {
			this.submitFeedback(true);
		}

		this.markDone();
	}

	override willDestroy(): void {
		super.willDestroy();

		if (!this.wasFeedbackSuccessfullySubmitted && this.isScoreSixOrLess) {
			this.submitFeedback();
		}

		if (this.isScoreSelected) {
			this.markDone();
		}
	}
	<template>
		<div class="nps-form {{if this.isStep1 'step-1' 'step-2'}}">
			<div class="title">
				{{#unless this.isStep1}}
					<CommonUiIconComponent
						@name="CheckCircleFill"
						@size="2xl"
						@class="nps-check-icon"
					/>
				{{/unless}}
				{{this.title}}
			</div>
			<a role="button" class="close" {{on "click" this.close}}>
				<CommonUiIconComponent @name="Close" @size="sm" />
			</a>
			<div class="content {{if this.isScoreInBetween 'content--blank'}}">
				{{#if this.isStep1}}
					<div class="scores {{if this.isScoreSelected 'scores--disabled'}}">
						<ul>
							{{#each this.scores as |score|}}
								<li
									role="button"
									class={{if (eq score this.selectedScore) "selected"}}
									{{on "click" (fn this.selectScore score)}}
								>
									{{score}}
								</li>
							{{/each}}
						</ul>
						<div>
							<span>Not at all likely</span>
							<span>Extremely likely</span>
						</div>
					</div>
					{{#if this.isScoreSixOrLess}}
						<div class="feedback">
							<div class="row">
								<label>What would you like to see improved?</label>
								{{#each this.choicesWithSelection as |choice|}}
									<FormCheckboxComponent
										@name={{choice.name}}
										@label={{choice.value}}
										@selected={{choice.selected}}
										@isDisabled={{this.submitInProgress}}
										@onChange={{fn this.selectChoice choice.value}}
									/>
								{{/each}}

								{{#if this.isFeedbackInputShown}}
									<label>What else would you like to see improved?</label>
									<FormInputComponent
										name="feedback"
										placeholder="Your feedback"
										maxlength="255"
										disabled={{this.submitInProgress}}
										@value={{this.feedback}}
										@hideFloatLabel={{true}}
									/>
								{{/if}}
							</div>
						</div>
					{{/if}}
				{{else}}
					{{#if this.isScoreSixOrLess}}
						<p>
							If you need any help or assistance, our customer support team is here to
							guide you!
						</p>
					{{else if this.isScoreNineOrMore}}
						<p>
							If you have a moment, we’d really appreciate a quick review on
							{{this.selectedReviewProvider.name}}. Your review helps us grow and
							allows more businesses like yours to discover Databox.
						</p>
						<img
							src="/images/svg/{{this.selectedReviewProvider.logo}}"
							width={{this.selectedReviewProvider.logoWidth}}
							height="auto"
							alt
						/>
					{{/if}}
				{{/if}}
			</div>
			<div class="controls2">
				<div class="controls2__right">
					{{#if this.isStep1}}
						{{#if this.isScoreSixOrLess}}
							{{#if this.isScoreSelected}}
								<SubmitButtonComponent
									@text="Submit feedback"
									@size="big"
									@noDelay={{true}}
									@onTrigger={{fn this.submitFeedback undefined}}
								/>
							{{/if}}
						{{/if}}
					{{else}}
						{{#if this.isScoreSixOrLess}}
							{{#if this.canRequestSetupHelp}}
								<SubmitButtonComponent
									@text="Book a call"
									@size="big"
									@noDelay={{true}}
									@onTrigger={{this.openRequestSetupHelp}}
								/>
							{{/if}}
						{{else if this.isScoreNineOrMore}}
							<a
								role="button"
								class="button size-big"
								{{on "click" this.openReviewProviderUrl}}
							>
								Write a
								{{this.selectedReviewProvider.name}}
								review
							</a>
						{{/if}}
					{{/if}}
				</div>
			</div>
		</div>
	</template>
}
